import {
	DndContext,
	DragEndEvent,
	PointerSensor,
	closestCenter,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
	SortableContext,
	arrayMove,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
	KeyboardEvent,
	ReactNode,
	Ref,
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
} from 'react';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { AddIcon, CloseIcon, ReorderIcon } from '../icons';
import { tokens } from '../theme/tokens';
import { FlexGap } from './FlexGap';
import { Paragraph } from './Text';
import { Button } from './button/Button';

const DeleteButton = styled.button`
	display: flex;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	width: 16px;
	height: 16px;
	padding: 0;
	margin: 0;
	border: 0;
	background: transparent;
	color: ${tokens.icon.tertiary};
	cursor: pointer;
	visibility: hidden;

	svg {
		display: block;
		width: 16px;
		height: 16px;
		color: inherit;
	}
`;

const ErrorText = styled(Paragraph)`
	color: ${({ theme }) => theme.colors.danger};
`;

const OptionContainer = styled(FlexGap)<{ $readOnly?: boolean }>`
	margin-left: -16px;
	margin-right: -16px;
	padding: 6px 16px;

	${({ $readOnly }) =>
		!$readOnly &&
		`
		&:hover {
			background-color: ${tokens.surface.hover};
		}
	`}

	&:hover ${DeleteButton},
	&:focus-within ${DeleteButton} {
		visibility: visible;
	}
`;

const DragHandle = styled(Flex)`
	width: 16px;
	height: 16px;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	cursor: grab;
	touch-action: none;
	color: ${tokens.icon.tertiary};

	&:active {
		cursor: grabbing;
	}

	svg {
		width: 16px;
		height: 16px;
		color: inherit;

		path {
			fill: currentColor;
		}
	}
`;

export type OptionListItem = { key: string };

export type OptionListHandle = {
	focusFirst: () => void;
};

/** Props handed to the consumer so it can wire up its own input element. */
export type OptionListInputProps = {
	autoFocus: boolean;
	onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
	onFocus: () => void;
	registerRef: (element: HTMLElement | null) => void;
};

function focusOptionInput(wrapper: HTMLElement | null | undefined) {
	const input = wrapper?.querySelector<HTMLElement>(
		'[contenteditable="true"], [contenteditable="plaintext-only"]',
	);

	if (!input) {
		return;
	}

	input.focus();

	const selection = window.getSelection();
	const range = document.createRange();
	range.selectNodeContents(input);
	range.collapse(false);
	selection?.removeAllRanges();
	selection?.addRange(range);
}

type RowProps<T extends OptionListItem> = {
	item: T;
	active: boolean;
	readOnly?: boolean;
	canRemove: boolean;
	leading?: ReactNode;
	renderInput: (item: T, props: OptionListInputProps) => ReactNode;
	inputProps: OptionListInputProps;
	onRemove: () => void;
	dragHandle?: ReactNode;
};

function OptionListRow<T extends OptionListItem>(props: RowProps<T>) {
	return (
		<OptionContainer alignItems="center" gap={6} $readOnly={props.readOnly}>
			{props.dragHandle}
			{props.leading}
			<Box flexGrow={1} flexShrink={1}>
				{props.renderInput(props.item, props.inputProps)}
			</Box>
			{props.active && !props.readOnly && props.canRemove && (
				<DeleteButton type="button" onClick={props.onRemove}>
					<CloseIcon size={16} />
				</DeleteButton>
			)}
		</OptionContainer>
	);
}

function SortableOptionListRow<T extends OptionListItem>(props: RowProps<T>) {
	const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
		id: props.item.key,
		animateLayoutChanges: () => false,
	});

	return (
		<div
			ref={setNodeRef}
			style={{ transform: CSS.Transform.toString(transform), transition }}
			{...attributes}
		>
			<OptionListRow
				{...props}
				dragHandle={
					<DragHandle {...listeners}>
						<ReorderIcon size={16} />
					</DragHandle>
				}
			/>
		</div>
	);
}

export type OptionListProps<T extends OptionListItem> = {
	items: T[];
	active: boolean;
	readOnly?: boolean;
	/** Minimum number of items to keep; removal is disabled at or below this count. Defaults to 1. */
	minItems?: number;
	/** Key of the item to auto-focus, matched against `item.key`. */
	autoFocusKey?: string;
	error?: string;
	addLabel: string;
	createItem: () => T;
	renderInput: (item: T, props: OptionListInputProps) => ReactNode;
	renderLeading?: (item: T) => ReactNode;
	onChange: (items: T[]) => void;
	onFocusItem?: (key: string) => void;
	/** Called when Shift+Tab is pressed on the first row, to move focus to the preceding field. */
	onFocusPrevious?: () => void;
	onAdd?: () => void;
};

/**
 * A keyboard-driven, reorderable list of options. Press Enter (or Tab) on the
 * last row to append a new option, and Backspace/Delete on an empty row to
 * remove it. Rows can be dragged to reorder. The row's input element is
 * supplied by the consumer via `renderInput`, so the list stays agnostic about
 * what an "option" contains.
 * @param props - The option list configuration and render slots.
 * @param ref - Handle exposing imperative focus controls.
 */
function OptionListInner<T extends OptionListItem>(
	props: OptionListProps<T>,
	ref: Ref<OptionListHandle>,
) {
	const minItems = props.minItems ?? 1;

	const sensors = useSensors(useSensor(PointerSensor));

	const inputRefs = useRef<Map<string, HTMLElement>>(new Map());
	const pendingFocusKey = useRef<string | null>(null);

	useImperativeHandle(
		ref,
		() => ({
			focusFirst: () => focusOptionInput(inputRefs.current.get(props.items[0]?.key ?? '')),
		}),
		[props.items],
	);

	useEffect(() => {
		const key = pendingFocusKey.current;

		if (key && inputRefs.current.has(key)) {
			focusOptionInput(inputRefs.current.get(key));
			pendingFocusKey.current = null;
		}
	});

	const removeItem = (key: string) => {
		props.onChange(props.items.filter((item) => item.key !== key));
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>, index: number) => {
		const goForward = event.key === 'Enter' || (event.key === 'Tab' && !event.shiftKey);
		const goBackward = event.key === 'Tab' && event.shiftKey;
		const remove = event.key === 'Backspace' || event.key === 'Delete';

		if (goForward) {
			event.preventDefault();

			if (index === props.items.length - 1) {
				const item = props.createItem();
				pendingFocusKey.current = item.key;
				props.onAdd?.();
				props.onChange([...props.items, item]);
			} else {
				focusOptionInput(inputRefs.current.get(props.items[index + 1].key));
			}
		} else if (goBackward && index > 0) {
			event.preventDefault();
			focusOptionInput(inputRefs.current.get(props.items[index - 1].key));
		} else if (goBackward && index === 0 && props.onFocusPrevious) {
			event.preventDefault();
			props.onFocusPrevious();
		} else if (
			remove &&
			props.active &&
			!props.readOnly &&
			props.items.length > minItems &&
			(event.currentTarget.textContent ?? '') === ''
		) {
			event.preventDefault();

			const focusKey = index > 0 ? props.items[index - 1].key : props.items[index + 1]?.key;

			removeItem(props.items[index].key);
			focusOptionInput(inputRefs.current.get(focusKey ?? ''));
		}
	};

	const onDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;

		if (over && active.id !== over.id) {
			const oldIndex = props.items.findIndex((item) => item.key === active.id);
			const newIndex = props.items.findIndex((item) => item.key === over.id);

			props.onChange(arrayMove(props.items, oldIndex, newIndex));
		}
	};

	const rowProps = (item: T, index: number): RowProps<T> => ({
		item,
		active: props.active,
		readOnly: props.readOnly,
		canRemove: props.items.length > minItems,
		leading: props.renderLeading?.(item),
		renderInput: props.renderInput,
		onRemove: () => removeItem(item.key),
		inputProps: {
			autoFocus: props.autoFocusKey === item.key,
			onFocus: () => props.onFocusItem?.(item.key),
			onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => handleKeyDown(event, index),
			registerRef: (element: HTMLElement | null) => {
				if (element) {
					inputRefs.current.set(item.key, element);
				} else {
					inputRefs.current.delete(item.key);
				}
			},
		},
	});

	return (
		<Box mt={2}>
			{props.active && !props.readOnly ? (
				<DndContext
					sensors={sensors}
					collisionDetection={closestCenter}
					modifiers={[restrictToVerticalAxis]}
					onDragEnd={onDragEnd}
				>
					<SortableContext
						items={props.items.map((item) => item.key)}
						strategy={verticalListSortingStrategy}
					>
						{props.items.map((item, index) => (
							<SortableOptionListRow key={item.key} {...rowProps(item, index)} />
						))}
					</SortableContext>
				</DndContext>
			) : (
				props.items.map((item, index) => (
					<OptionListRow key={item.key} {...rowProps(item, index)} />
				))
			)}
			{props.active && !props.readOnly && (
				<>
					{props.error && <ErrorText mt={2}>{props.error}</ErrorText>}
					<Flex mt={2}>
						<Button
							small
							type="secondary"
							icon={<AddIcon />}
							title={props.addLabel}
							onClick={() => {
								props.onAdd?.();
								props.onChange([...props.items, props.createItem()]);
							}}
						/>
					</Flex>
				</>
			)}
		</Box>
	);
}

export const OptionList = forwardRef(OptionListInner) as <T extends OptionListItem>(
	props: OptionListProps<T> & { ref?: Ref<OptionListHandle> },
) => ReturnType<typeof OptionListInner>;
